/**
 * @lodex-pro/web-shared/auth/amplify-bootstrap — idempotent Amplify v6 init for web.
 *
 * Phase 4 / Plan 04-09 — wires the three pieces that turn a fresh page load into a
 * working Amplify v6 client:
 *
 *   1. `Amplify.configure(buildAmplifyConfig(pool))` — Amplify learns about the tenant
 *      type's user pool + client id. `buildAmplifyConfig` is the shared helper from
 *      `@lodex-pro/shared/auth/amplify-config` (D-05) that takes ONE PoolConfigEntry
 *      and returns the Amplify ResourcesConfig literal.
 *
 *   2. `cognitoUserPoolsTokenProvider.setKeyValueStorage(new CookieExchangeStorageAdapter(...))`
 *      — Amplify uses our adapter for refresh + access token storage. The adapter
 *      intercepts `.refreshToken` keys and routes them through the server-side
 *      cookie-exchange endpoint (D-07 + RESEARCH Pattern 5).
 *
 *   3. The function is idempotent — repeated calls are a no-op. Web apps may re-render
 *      the App component on hot reload; calling `Amplify.configure` twice is a noisy
 *      no-op but the storage adapter MUST NOT be re-installed (it would lose the
 *      in-memory access token from the prior install).
 *
 * Phase 4 / Plan 04-11 (CR-06 fix) — TWO additions:
 *   - Module-level singletons `webAmplifyAdapter` + `webAmplifyClientId` retain
 *     references to the configured adapter + the active pool's clientId, so the
 *     api-client's 401-refresh-retry path can write a fresh access token directly
 *     into the adapter's in-memory Map under the canonical Amplify v6 storage key
 *     without going through `Amplify.configure` again.
 *   - `primeAccessTokenFromRefresh(accessToken)` is the public helper the api-client
 *     calls after a successful POST /v1/auth/refresh. It decodes the JWT's `sub`
 *     claim, builds the canonical key (`CognitoIdentityServiceProvider.<clientId>.<sub>.accessToken`),
 *     and calls `adapter.primeMemoryEntry(key, accessToken)`. The decoder is
 *     permissive — on any parse error it returns null and the helper no-ops (the
 *     api-client's retry hop also attaches the new access token via Bearer directly,
 *     so a no-op here does not break the retry).
 *
 * The web app consumer (plan 04-10 wires the 4 apps) calls `bootstrapWebAmplify` exactly
 * once at module load before any auth-related code runs. The pool config array comes
 * from the API's `GET /v1/auth/config` endpoint (or a build-time injected env var —
 * plan 04-10 picks the canonical source).
 *
 * Spec: master spec §7 (web auth); CONTEXT D-05 (Amplify v6) + D-07 (cookie exchange);
 *       RESEARCH § Pattern 4 (Amplify storage adapter wiring) + § Pattern 5;
 *       Plan 04-11 — CR-06 fix.
 */

import { Amplify } from 'aws-amplify';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import {
  buildAmplifyConfig,
  selectPoolByTenantType,
  type AmplifyTenantType,
  type PoolConfigEntry,
} from '@lodex-pro/shared/auth/amplify-config';

import { CookieExchangeStorageAdapter } from './cookie-storage-adapter.js';
import { __resetRefreshGateForTesting, refreshViaCookie } from './refresh-gate.js';

/**
 * Module-level guard that prevents double-bootstrap. React strict-mode double-render
 * and Vite hot-reload both can trigger a re-invocation; we accept the first config and
 * silently ignore subsequent calls. The first-call cache is what guarantees the
 * in-memory access token (held by the storage adapter) survives a hot reload.
 */
let configured = false;

/**
 * Phase 4 / Plan 04-11 (CR-06 fix) — module-level singleton references retained at
 * first bootstrap. `primeAccessTokenFromRefresh` reads BOTH to build the canonical
 * Amplify v6 storage key + write into the in-memory map.
 *
 * Cleared by `__resetWebAmplifyBootstrapForTesting` so per-test isolation can
 * re-bootstrap with a fresh adapter without leaking module state across tests.
 */
let webAmplifyAdapter: CookieExchangeStorageAdapter | null = null;
let webAmplifyClientId: string | null = null;
// Quick 260614-loop — the API origin, captured so useAuth can restore a session
// from the HttpOnly refresh cookie on boot (POST /v1/auth/refresh).
let webAmplifyApiBaseUrl: string | null = null;

/**
 * Input for `bootstrapWebAmplify`. The 4-entry `pools` array is sourced from the API
 * (or build-time env) per CONTEXT D-03; `tenantType` is the discriminator that picks
 * which pool's client id to feed Amplify.configure.
 *
 * `apiBaseUrl` MUST match the origin the page is served from (same-origin requirement
 * for the SameSite=Strict refresh cookie). In dev this is `http://localhost:3000`; in
 * prod it's the deployed API origin (e.g. `https://api.lodex.pro`).
 */
export interface BootstrapWebAmplifyInput {
  readonly pools: ReadonlyArray<PoolConfigEntry>;
  readonly tenantType: AmplifyTenantType;
  readonly apiBaseUrl: string;
}

/**
 * Bootstrap Amplify v6 for the web. Idempotent — repeated calls return without effect.
 *
 * Throws (via `selectPoolByTenantType`) if `pools.length !== 4` (positional contract
 * violation — refuses to silently pick the wrong pool).
 *
 * Plan 04-11 (CR-06 fix): retains `webAmplifyAdapter` + `webAmplifyClientId` as
 * module-level singletons so `primeAccessTokenFromRefresh` can write to the
 * configured adapter without re-bootstrapping.
 */
export function bootstrapWebAmplify(input: BootstrapWebAmplifyInput): void {
  if (configured) {
    return;
  }
  const pool = selectPoolByTenantType(input.pools, input.tenantType);
  const adapter = new CookieExchangeStorageAdapter({ apiBaseUrl: input.apiBaseUrl });
  webAmplifyAdapter = adapter;
  webAmplifyClientId = pool.clientId;
  webAmplifyApiBaseUrl = input.apiBaseUrl;
  Amplify.configure(buildAmplifyConfig(pool));
  cognitoUserPoolsTokenProvider.setKeyValueStorage(adapter);
  configured = true;
}

/**
 * Decode the `sub` claim from a JWT access token without pulling `jose`.
 *
 * Plan 04-11 (CR-06 fix): the api-client's 401-refresh-retry path needs the `sub`
 * claim of the FRESH access token returned by POST /v1/auth/refresh so it can
 * write the token under the canonical Amplify v6 storage key:
 *   CognitoIdentityServiceProvider.<clientId>.<sub>.accessToken
 *
 * Base64url-decodes the middle segment (handles +/-_/= padding). On any parse error
 * returns null — `primeAccessTokenFromRefresh` no-ops in that case (the api-client
 * retry hop ALSO attaches the new Bearer directly, so a failed decode does not
 * break the retry, it just leaves Amplify's in-memory cache one tick behind).
 *
 * No new dep added (the plan explicitly avoids pulling `jose` into web-shared).
 * `atob` is available in both browser (Vite) and jsdom (Vitest) runtimes; the
 * `Buffer` branch is unreachable in practice but keeps the helper TS-strict.
 */
function decodeAccessTokenSub(token: string): string | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const middle = parts[1];
    if (middle === undefined || middle.length === 0) return null;
    // Base64url → Base64 (+/-_/= padding)
    const b64 = middle.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    let decoded: string;
    if (typeof atob === 'function') {
      decoded = atob(padded);
    } else {
      // Server-side / non-browser fallback (unused in practice; Vitest jsdom has atob).
      const bufCtor = (globalThis as { Buffer?: typeof Buffer }).Buffer;
      if (bufCtor === undefined) return null;
      decoded = bufCtor.from(padded, 'base64').toString('binary');
    }
    const payload = JSON.parse(decoded) as { sub?: unknown };
    return typeof payload.sub === 'string' && payload.sub.length > 0 ? payload.sub : null;
  } catch {
    return null;
  }
}

/**
 * Phase 4 / Plan 04-11 (CR-06 fix). Prime Amplify's in-memory access-token cache
 * after a successful POST /v1/auth/refresh. Writes the new access token under the
 * Amplify v6 storage key Amplify will read:
 *   CognitoIdentityServiceProvider.<clientId>.<LastAuthUser>.accessToken
 * adopting the token's `sub` as LastAuthUser when the tab has none (see below).
 *
 * No-ops if the bootstrap hasn't run yet (adapter or clientId not set) OR the
 * access token cannot be parsed for its `sub` claim. The api-client's retry hop
 * ALSO attaches the new access token via Authorization: Bearer directly, so a
 * no-op here does not break the retry — Amplify just stays one tick behind its
 * in-memory cache (the next fetchAuthSession() call lands on the primed value).
 *
 * Web-shared-internal contract: the cookie-storage adapter's `primeMemoryEntry`
 * method is the only path to write under the canonical key. This helper is the
 * only caller of primeMemoryEntry. Consumer apps import `authedFetch` from
 * `./api-client.js` — not this helper directly.
 */
export function primeAccessTokenFromRefresh(accessToken: string): void {
  if (webAmplifyAdapter === null || webAmplifyClientId === null) return;
  const sub = decodeAccessTokenSub(accessToken);
  if (sub === null) return;

  // ── Write where Amplify will READ it ───────────────────────────────────────
  // Amplify 6 does not key its tokens by `sub`: `TokenStore.getAuthKeys()` uses
  // the stored `LastAuthUser`, falling back to the literal 'username' when there
  // is none. Priming under `<sub>` reached Amplify only when LastAuthUser was
  // present AND equal to the sub — and it is absent after every page load, the
  // post-login redirect included, because the adapter's memory is per-tab. So
  // `fetchAuthSession()` never saw a refreshed token: every `authedFetch` went
  // out with no Bearer, took a 401 and a refresh, then retried. Measured on dev
  // 2026-10-08: 25 of 50 calls in a minute of ordinary navigation. On a screen
  // that polls (the import run step, every 1.5 s) that outruns
  // /v1/auth/refresh's 30-per-minute IP limit, and a refused refresh surfaces the
  // ORIGINAL 401 — the intermittent "Sign in to continue" on the import preview.
  //
  // Keep an existing LastAuthUser (a mid-session refresh: the keys Amplify
  // already reads), else adopt the sub so the keys line up after a reload.
  const lastAuthUserKey = `CognitoIdentityServiceProvider.${webAmplifyClientId}.LastAuthUser`;
  let user = webAmplifyAdapter.peekMemoryEntry(lastAuthUserKey);
  if (user === null) {
    user = sub;
    webAmplifyAdapter.primeMemoryEntry(lastAuthUserKey, user);
  }
  const prefix = `CognitoIdentityServiceProvider.${webAmplifyClientId}.${user}`;
  webAmplifyAdapter.primeMemoryEntry(`${prefix}.accessToken`, accessToken);
  // The refresh returns no id token, and the sign-in one expires with the access
  // token it came with. Left in place, Amplify sees it expired and attempts its
  // own refresh, which cannot work (the refresh token is an HttpOnly cookie), so
  // it never returns the token primed above. No web code reads the id token.
  webAmplifyAdapter.dropMemoryEntry(`${prefix}.idToken`);
}

/**
 * Quick 260614-loop — restore an in-memory session from the HttpOnly refresh cookie.
 *
 * The refresh token lives in the `lodex_refresh` HttpOnly cookie (set by the
 * cookie-exchange during signIn) — JS / Amplify CANNOT read it, and the access +
 * id tokens are kept in-memory ONLY by CookieExchangeStorageAdapter, so they are
 * gone after a full page reload. That left `useAuth`'s boot-time getCurrentUser /
 * fetchAuthSession with no tokens → unauthenticated → the post-login redirect loop.
 *
 * This does the same thing the api-client's 401 path does, but proactively at boot:
 * POST {apiBaseUrl}/v1/auth/refresh (the cookie auto-attaches, Path=/v1/auth/refresh),
 * and on a 2xx body prime Amplify's in-memory access-token cache. Returns the new
 * access token (so the caller can mark the session authenticated) or null when there
 * is no valid refresh cookie (genuinely signed out). Never throws.
 *
 * OPEN-193: it goes through the tab's shared refresh gate (refresh-gate.ts), so it shares
 * one in-flight request with authedFetch and backs off after a 429 instead of retrying.
 */
export function restoreSessionFromRefreshCookie(): Promise<string | null> {
  if (webAmplifyApiBaseUrl === null) return Promise.resolve(null);
  return refreshViaCookie(webAmplifyApiBaseUrl, primeAccessTokenFromRefresh);
}

/**
 * Test-only escape hatch — resets the `configured` guard so `bootstrapWebAmplify` can
 * be exercised more than once within a single Vitest run. Production code MUST NOT
 * call this; it exists so per-test isolation can re-bootstrap with a fresh adapter.
 *
 * Plan 04-11 (CR-06 fix): also clears the `webAmplifyAdapter` + `webAmplifyClientId`
 * singletons so a per-test bootstrap starts from a clean slate.
 *
 * Mirrors `__resetEnrollmentTokenKeyCacheForTesting` from
 * `packages/api/src/auth/enrollment-token.ts` (plan 04-06) and
 * `__resetCognitoPoolConfigCacheForTesting` from
 * `packages/api/src/cognito/pool-config.ts` (plan 04-05). Same testability story.
 */
export function __resetWebAmplifyBootstrapForTesting(): void {
  configured = false;
  webAmplifyAdapter = null;
  webAmplifyClientId = null;
  webAmplifyApiBaseUrl = null;
  __resetRefreshGateForTesting(); // OPEN-193: the shared refresh gate starts clean too
}
